import React, { useState, useEffect, useMemo } from 'react';
import Sidebar from '../components/Sidebar';
import { FaDownload, FaSort, FaSortUp, FaSortDown, FaChevronLeft, FaChevronRight, FaFilePdf } from 'react-icons/fa';
import jsPDF from 'jspdf';
import { autoTable } from 'jspdf-autotable';
import { useNavigate } from 'react-router-dom';

const API_URL = '/api';

const StockSummaryReport = () => {
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [selectedLocation, setSelectedLocation] = useState('');
  const navigate = useNavigate();
  const [locations, setLocations] = useState([]);
  const [summaryData, setSummaryData] = useState([]);
  const [products, setProducts] = useState([]);
  const [uoms, setUoms] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(true);
  const [dateError, setDateError] = useState('');

  // --- THEME HOOKS (must run unconditionally) ---
  // Use the page's primary/dark color (used in this file) as accent so sidebar active/profile/logout match.
  const pageTheme = useMemo(() => ({
    bg: '#f3f4f6',
    sidebarBg: '#ffffff',
    accent: '#2c3e50', // matches buttons/focus colors in this file
    border: '#d1d5db',
    text: '#0f1724',
    muted: '#6b7280',
    iconColor: '#2c3e50',
    hoverBg: '#eff6ff',
    logout: '#2c3e50' // use same accent for profile/logout
  }), []);

  // Inject CSS variables and scoped stylesheet so Sidebar matches pageTheme
  useEffect(() => {
    const themeVars = {
      '--sidebar-line-color': pageTheme.border,
      '--sidebar-bg': pageTheme.sidebarBg,
      '--sidebar-text': pageTheme.text,
      '--accent-color': pageTheme.accent,
      '--accent-hover': pageTheme.accent,
      '--page-border-color': pageTheme.border,
      '--page-bg': pageTheme.bg,
      '--text-primary': pageTheme.text,
      '--text-muted': pageTheme.muted,
      '--icon-color': pageTheme.iconColor,
      '--hover-bg': pageTheme.hoverBg,
      '--sidebar-active-text': pageTheme.accent,
      '--sidebar-profile-bg': pageTheme.logout,
      '--logout-bg': pageTheme.logout
    };

    Object.entries(themeVars).forEach(([k, v]) => document.documentElement.style.setProperty(k, v));

    const styleId = 'stock-summary-sidebar-overrides';
    const existing = document.getElementById(styleId);
    if (existing) existing.remove();

    const style = document.createElement('style');
    style.id = styleId;
    style.innerHTML = `
      
    `;

    document.head.appendChild(style);

    return () => {
      const el = document.getElementById(styleId);
      if (el) el.remove();
    };
  }, [pageTheme]);
  // --- end theme hooks ---

  // Function to convert date from calendar format to the required UTC format
  const convertToUTCFormat = (dateStr, isEndDate = false) => {
    try {
      // Create a date object from the input string (YYYY-MM-DD format from date picker)
      const date = new Date(dateStr);

      console.log("printing  date..." + date);
      
      // Check if date is valid
      if (isNaN(date.getTime())) {
        throw new Error(`Invalid date format: ${dateStr}`);
      }
      
      // Extract date components
      const year = date.getFullYear();
      const month = date.getMonth();
      const day = date.getDate();

      console.log("printing year..." + year + " month..." + month + " day..." + day);
      
      // Create a new date object in local timezone
      const localDate = new Date(year, month, day);
      
      // Set time components based on whether it's start or end date
      if (isEndDate) {
        // For end date: set to 23:59:59.999 local time
        localDate.setHours(23, 59, 59, 999);
      } else {
        // For start date: set to 00:00:00.000 local time
        localDate.setHours(0, 0, 0, 0);
      }
      
      // Convert to UTC and return the ISO string
      return localDate.toISOString();
    } catch (error) {
      console.error('Date conversion error:', error);
      throw error;
    }
  };

  // Fetch locations on component mount
  useEffect(() => {
    fetchLocations();
    fetchProducts();
    fetchUoms();
  }, []);

  const fetchLocations = async () => {
    try {
      const response = await fetch(`${API_URL}/locations`);
      if (!response.ok) throw new Error('Failed to fetch locations');
      const data = await response.json();
      setLocations(data);
    } catch (error) {
      console.error('Error fetching locations:', error);
    }
  };

  const fetchProducts = async () => {
    try {
      const response = await fetch(`${API_URL}/products`);
      if (!response.ok) throw new Error('Failed to fetch products');
      const data = await response.json();
      setProducts(data);
    } catch (error) {
      console.error('Error fetching products:', error);
    }
  };

  const fetchUoms = async () => {
    try {
      const response = await fetch(`${API_URL}/uoms`);
      if (!response.ok) throw new Error('Failed to fetch UOMs');
      const data = await response.json();
      setUoms(data);
    } catch (error) {
      console.error('Error fetching UOMs:', error);
    }
  };

  const fetchSummaryData = async () => {
    if (!fromDate || !toDate) {
      alert('Please select both from and to dates');
      return;
    }

    // Reset any previous errors
    setDateError('');
    setLoading(true);
    
    try {
      // Convert dates to the required UTC format
      const formattedFromDate = convertToUTCFormat(fromDate);
      const formattedToDate = convertToUTCFormat(toDate, true);
      
      console.log('Formatted dates:', {
        fromDate: formattedFromDate,
        toDate: formattedToDate
      });
      
      const params = new URLSearchParams({
        fromDate: formattedFromDate,
        toDate: formattedToDate,
        ...(selectedLocation && { warehouse: selectedLocation })
      });
      
      const response = await fetch(`${API_URL}/inventory-summary?${params}`);
      if (!response.ok) throw new Error('Failed to fetch summary data');
      const data = await response.json();
      
      // Process data to include product details
      const processedData = data.map(item => {
        const product = products.find(p => p.ProductCode === item.ItemCode);
        const uom = uoms.find(u => u.UOMID === product?.UOMID);
        const location = locations.find(l => l.LocationCode === selectedLocation);
        
        return {
          ...item,
          ProductName: product?.ProductName || 'N/A',
          ProductDescription: product?.Description || 'N/A',
          UOMName: uom?.UOMName || 'N/A',
          LocationCode: selectedLocation || 'ALL',
          LocationName: location?.LocationName || 'All Warehouse',
          // IssueQty is negative in API, convert to positive for display but keep the value
          IssueQtyDisplay: Math.abs(item.IssueQty),
          // Keep original values for calculations
          IssueQty: item.IssueQty
        };
      });
      
      setSummaryData(processedData);
      setCurrentPage(1); // Reset to first page when new data is loaded
    } catch (error) {
      console.error('Error fetching summary data:', error);
      if (error.message.includes('Invalid date format')) {
        setDateError('Please select valid dates from the calendar');
      } else {
        alert('Error fetching summary data');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleGeneratePDF = () => {
    if (summaryData.length === 0) {
      alert('No data to generate PDF');
      return;
    }

    setPdfLoading(true);
    try {
      // Create a new jsPDF instance in landscape orientation for better table fit
      const doc = new jsPDF('landscape');
      
      // Set document properties
      doc.setProperties({
        title: 'Stock Summary Report',
        subject: 'Inventory Summary',
        author: 'Inventory Portal',
        creator: 'Inventory Portal'
      });
      
      // Define colors
      const primaryColor = [41, 98, 155]; // Dark blue
      const secondaryColor = [245, 245, 245]; // Light gray
      const accentColor = [66, 139, 202]; // Medium blue
      
      // Add header with professional styling
      doc.setFillColor(...primaryColor);
      doc.rect(0, 0, doc.internal.pageSize.width, 40, 'F');
      
      // Add title in header
      doc.setFontSize(22);
      doc.setTextColor(255, 255, 255);
      doc.text('Stock Summary Report', doc.internal.pageSize.width / 2, 20, { align: 'center' });
      
      // Add subtitle
      doc.setFontSize(12);
      doc.text('Inventory Management System', doc.internal.pageSize.width / 2, 30, { align: 'center' });
      
      // Add report details section
      doc.setTextColor(0, 0, 0);
      doc.setFontSize(11);
      doc.text(`Report Period: ${fromDate} to ${toDate}`, 20, 55);
      doc.text(`Warehouse: ${selectedLocation ? `${selectedLocation} - ${locations.find(l => l.LocationCode === selectedLocation)?.LocationName || 'N/A'}` : 'All Warehouse'}`, 20, 62);
      
      // Add generation timestamp
      const now = new Date();
      doc.text(`Generated on: ${now.toLocaleDateString()} at ${now.toLocaleTimeString()}`, 20, 69);
      
      // Add summary section
      const totalOpening = summaryData.reduce((sum, item) => sum + (item.OpeningQty || 0), 0);
      const totalReceipt = summaryData.reduce((sum, item) => sum + (item.ReceiptQty || 0), 0);
      const totalIssue = summaryData.reduce((sum, item) => sum + Math.abs(item.IssueQty || 0), 0);
      const totalClosing = summaryData.reduce((sum, item) => sum + (item.ClosingQty || 0), 0);
      
      // Prepare table data with proper formatting
      const tableData = summaryData.map(item => [
        item.LocationName,
        item.ItemCode,
        item.ProductName,
        item.ProductDescription,
        item.UOMName,
        item.OpeningQty,
        item.ReceiptQty,
        item.IssueQtyDisplay,
        item.ClosingQty
      ]);
      
      // Add table with professional styling
      autoTable(doc, {
        head: [['Warehouse', 'Product Code', 'Product Name', 'Description', 'UOM', 'Opening Qty', 'Receipt Qty', 'Issue Qty', 'Closing Qty']],
        body: tableData,
        startY: 80,
        styles: {
          fontSize: 9,
          cellPadding: 4,
          valign: 'middle',
          halign: 'left',
          font: 'helvetica'
        },
        headStyles: {
          fillColor: accentColor,
          textColor: 255,
          fontStyle: 'bold',
          halign: 'center',
          fontSize: 10
        },
        alternateRowStyles: {
          fillColor: [248, 248, 248]
        },
        columnStyles: {
          0: { cellWidth: 30, halign: 'left' }, // Location
          1: { cellWidth: 30, halign: 'left' }, // Product Code
          2: { cellWidth: 35, halign: 'left' }, // Product Name
          3: { cellWidth: 50, halign: 'left' }, // Description
          4: { cellWidth: 20, halign: 'center' }, // UOM
          5: { cellWidth: 25, halign: 'right' }, // Opening Qty
          6: { cellWidth: 25, halign: 'right' }, // Receipt Qty
          7: { cellWidth: 25, halign: 'right' }, // Issue Qty
          8: { cellWidth: 25, halign: 'right' }  // Closing Qty
        },
        margin: { top: 110, left: 20, right: 20 },
        theme: 'grid'
      });
      
      // Add footer with page numbers and company info
      const pageCount = doc.internal.getNumberOfPages();
      for (let i = 1; i <= pageCount; i++) {
        doc.setPage(i);
        
        // Add footer line
        doc.setDrawColor(200, 200, 200);
        doc.line(20, doc.internal.pageSize.height - 15, doc.internal.pageSize.width - 20, doc.internal.pageSize.height - 15);
        
        // Add page number
        doc.setFontSize(9);
        doc.setTextColor(100, 100, 100);
        doc.text(`Page ${i} of ${pageCount}`, doc.internal.pageSize.width - 30, doc.internal.pageSize.height - 10, { align: 'right' });
        
        // Add company info
        doc.text('Inventory Portal © 2025', 20, doc.internal.pageSize.height - 10);
      }
      
      // Save the PDF with a professional filename
      doc.save(`Stock_Summary_Report_${fromDate.replace(/-/g, '')}_to_${toDate.replace(/-/g, '')}.pdf`);
    } catch (error) {
      console.error('Error generating PDF:', error);
      alert('Error generating PDF');
    } finally {
      setPdfLoading(false);
    }
  };

  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  const getSortedData = (data) => {
    if (!sortConfig.key) return data;
    
    return [...data].sort((a, b) => {
      let aValue = a[sortConfig.key];
      let bValue = b[sortConfig.key];
      
      // Handle numeric values
      if (typeof aValue === 'number' && typeof bValue === 'number') {
        return sortConfig.direction === 'asc' ? aValue - bValue : bValue - aValue;
      }
      
      // Handle string values
      aValue = aValue || '';
      bValue = bValue || '';
      
      if (aValue < bValue) {
        return sortConfig.direction === 'asc' ? -1 : 1;
      }
      if (aValue > bValue) {
        return sortConfig.direction === 'asc' ? 1 : -1;
      }
      return 0;
    });
  };

   const handleLogout = () => {
    navigate('/login');
  };

  const handleDownloadCSV = () => {
    if (summaryData.length === 0) {
      alert('No data to export');
      return;
    }
    
    let csvContent = "Warehouse,Warehouse Code,Product Code,Product Name,Description,UOM,Opening Qty,Receipt Qty,Issue Qty,Closing Qty\n";
    
    summaryData.forEach(item => {
      csvContent += `${item.LocationName},${item.LocationCode},${item.ItemCode},${item.ProductName},"${item.ProductDescription}",${item.UOMName},${item.OpeningQty},${item.ReceiptQty},${item.IssueQty},${item.ClosingQty}\n`;
    });
    
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('download', `stock_summary_${fromDate}_to_${toDate}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Pagination logic
  const sortedData = getSortedData(summaryData);
  const totalPages = Math.ceil(sortedData.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentItems = sortedData.slice(startIndex, endIndex);

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const renderSortArrows = (columnName) => {
    const isSorted = sortConfig.key === columnName;
    const isAsc = isSorted && sortConfig.direction === 'asc';
    const isDesc = isSorted && sortConfig.direction === 'desc';
    
    return (
      <span className="ml-1 inline-flex flex-col">
        <span className={`text-xs ${isAsc ? 'text-gray-800 font-bold' : 'text-gray-800'}`}>
          <FaSortUp/>
        </span>
        <span className={`text-xs -mt-1 ${isDesc ? 'text-gray-800 font-bold' : 'text-gray-800'}`} style={{marginTop: '-0.45rem'}}>
          <FaSortDown/>
        </span>
      </span>
    );
  };

  const renderPaginationButtons = () => {
    const pages = [];
    const startPage = Math.max(1, currentPage - 2);
    const endPage = Math.min(totalPages, currentPage + 2);
    
    // Previous button
    pages.push(
      <button
        key="prev"
        className="p-1 rounded-md hover:bg-gray-200"
        onClick={() => handlePageChange(currentPage - 1)}
        disabled={currentPage === 1}
      >
        <FaChevronLeft className="w-3 h-3" />
      </button>
    );
    
    // Page numbers
    for (let i = startPage; i <= endPage; i++) {
      pages.push(
        <button
          key={i}
          className={`px-2 py-1 rounded-md transition-colors ${
            currentPage === i
              ? "bg-primary-500 text-white font-semibold"
              : "hover:bg-gray-200"
          }`}
          onClick={() => handlePageChange(i)}
        >
          {i}
        </button>
      );
    }
    
    // Next button
    pages.push(
      <button
        key="next"
        className="p-1 rounded-md hover:bg-gray-200"
        onClick={() => handlePageChange(currentPage + 1)}
        disabled={currentPage === totalPages || totalPages === 0}
      >
        <FaChevronRight className="w-3 h-3" />
      </button>
    );
    
    return pages;
  };

  // compute sidebar width (px) so main can size itself responsively when sidebar toggles
  const sidebarWidthPx = sidebarCollapsed ? 90 : 280;

  return (
    <div className="min-h-screen flex" style={{ backgroundColor: pageTheme.bg, '--page-border-color': pageTheme.border, '--page-background': pageTheme.bg }}>
      <Sidebar collapsed={sidebarCollapsed}
        onToggle={setSidebarCollapsed} theme={{ ...pageTheme, activeBg: pageTheme.accent, activeText: '#ffffff', profileBg: pageTheme.logout, logoutBg: pageTheme.logout }} onLogout={handleLogout} />
      <main className={`flex-1 p-8 overflow-hidden transition-all duration-300`} style={{ marginLeft: `${sidebarWidthPx}px`, width: `calc(100% - ${sidebarWidthPx}px)`, minWidth: 0 }}>
        {/* Header */}
        <div className="mb-8">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
            <div>
              <h1 className="text-3xl font-bold text-dark-500 mb-2" style={{ color: pageTheme.text }}>
                Stock Summary Report
              </h1>
              <p className="text-gray-600" style={{ color: pageTheme.muted }}>
                Generate inventory summary reports with date range and location filters.
              </p>
            </div>
            <div className="flex items-center gap-3">
              <button
                onClick={handleDownloadCSV}
                disabled={summaryData.length === 0}
                className="btn-secondary flex items-center gap-2 disabled:opacity-50"
                style={{ color: pageTheme.text }}
              >
                <FaDownload className="w-4 h-4" />
                Export CSV
              </button>
              <button
                onClick={handleGeneratePDF}
                disabled={pdfLoading || summaryData.length === 0}
                className="bg-red-600 hover:bg-red-700 text-white font-semibold px-4 py-2 rounded-lg shadow flex items-center gap-2 disabled:opacity-50"
              >
                {pdfLoading ? (
                  <>
                    <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Generating...
                  </>
                ) : (
                  <>
                    <FaFilePdf className="w-4 h-4" />
                    Generate PDF
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
        
        {/* Filters */}
        <div className="modern-card p-6 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div>
              <label className="block text-gray-700 text-sm font-bold mb-2">
                From Date
              </label>
              <input
                type="date"
                value={fromDate}
                onChange={(e) => setFromDate(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#2c3e50]"
              />
            </div>
            <div>
              <label className="block text-gray-700 text-sm font-bold mb-2">
                To Date
              </label>
              <input
                type="date"
                value={toDate}
                onChange={(e) => setToDate(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#2c3e50]"
              />
            </div>
            <div>
              <label className="block text-gray-700 text-sm font-bold mb-2">
                Warehouse (Optional)
              </label>
              <select
                value={selectedLocation}
                onChange={(e) => setSelectedLocation(e.target.value)}
                className="w-full py-2 px-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#2c3e50]"
              >
                <option value="">All Warehouse</option>
                {locations.map(location => (
                  <option key={location.LocationID} value={location.LocationCode}>
                    {location.LocationCode} - {location.LocationName}
                  </option>
                ))}
              </select>
            </div>
            <div className="flex items-end">
              <button 
                onClick={fetchSummaryData}
                disabled={loading}
                className="bg-[#2c3e50] hover:bg-[#34495e] text-white font-semibold px-6 py-2 rounded-lg shadow w-full disabled:opacity-50"
              >
                {loading ? 'Loading...' : 'Generate Report'}
              </button>
            </div>
          </div>
          {dateError && (
            <div className="mt-4 text-red-500 text-sm">
              {dateError}
            </div>
          )}
         
        </div>
        
        {/* Results Table */}
        <div className="bg-white p-6 rounded-2xl shadow-lg overflow-x-auto" style={{ borderColor: pageTheme.border }}>
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-xl font-bold text-[#2c3e50]" style={{ color: pageTheme.text }}>
              Stock Summary Report
            </h2>
            <div className="flex items-center space-x-2">
              <div className="flex items-center space-x-1 text-gray-500">
                {renderPaginationButtons()}
              </div>
            </div>
          </div>
          
          {loading ? (
            <div className="text-center py-8">
              <p>Loading report data...</p>
            </div>
          ) : summaryData.length === 0 ? (
            <div className="text-center py-8">
              <p className="text-gray-500">No data found for the selected criteria</p>
            </div>
          ) : (
            <>
              <div className="mb-4 text-sm text-gray-600">
                Showing {startIndex + 1} to {Math.min(endIndex, sortedData.length)} of {sortedData.length} records
              </div>
              <table className="w-full text-sm text-left border-separate border-spacing-y-2">
                <thead className="bg-gray-200 text-gray-700 uppercase text-sm font-semibold">
                  <tr>
                    <th 
                      className="px-4 py-3 cursor-pointer hover:bg-[#a8cff9]" 
                      onClick={() => handleSort('LocationName')}
                    >
                      <div className="flex items-center">
                        Warehouse
                        {renderSortArrows('LocationName')}
                      </div>
                    </th>
                    <th 
                      className="px-4 py-3 cursor-pointer hover:bg-[#a8cff9]" 
                      onClick={() => handleSort('ItemCode')}
                    >
                      <div className="flex items-center">
                        Product Code
                        {renderSortArrows('ItemCode')}
                      </div>
                    </th>
                    <th 
                      className="px-4 py-3 cursor-pointer hover:bg-[#a8cff9]" 
                      onClick={() => handleSort('ProductName')}
                    >
                      <div className="flex items-center">
                        Product Name
                        {renderSortArrows('ProductName')}
                      </div>
                    </th>
                    <th className="px-4 py-3">Description</th>
                    <th className="px-4 py-3 text-center">UOM</th>
                    <th 
                      className="px-4 py-3 cursor-pointer hover:bg-[#a8cff9] text-right" 
                      onClick={() => handleSort('OpeningQty')}
                    >
                      <div className="flex items-center justify-end">
                        Opening Qty
                        {renderSortArrows('OpeningQty')}
                      </div>
                    </th>
                    <th 
                      className="px-4 py-3 cursor-pointer hover:bg-[#a8cff9] text-right" 
                      onClick={() => handleSort('ReceiptQty')}
                    >
                      <div className="flex items-center justify-end">
                        Receipt Qty
                        {renderSortArrows('ReceiptQty')}
                      </div>
                    </th>
                    <th 
                      className="px-4 py-3 cursor-pointer hover:bg-[#a8cff9] text-right" 
                      onClick={() => handleSort('IssueQty')}
                    >
                      <div className="flex items-center justify-end">
                        Issue Qty
                        {renderSortArrows('IssueQty')}
                      </div>
                    </th>
                    <th 
                      className="px-4 py-3 cursor-pointer hover:bg-[#a8cff9] text-right" 
                      onClick={() => handleSort('ClosingQty')}
                    >
                      <div className="flex items-center justify-end">
                        Closing Qty
                        {renderSortArrows('ClosingQty')}
                      </div>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {currentItems.map((item, index) => (
                    <tr key={index} className="bg-white hover:bg-gray-100 transition">
                      <td className="px-4 py-3 font-medium" style={{ color: pageTheme.text }}>
                        {item.LocationName} ({item.LocationCode})
                      </td>
                      <td className="px-4 py-3" style={{ color: pageTheme.muted }}>{item.ItemCode}</td>
                      <td className="px-4 py-3" style={{ color: pageTheme.muted }}>{item.ProductName}</td>
                      <td className="px-4 py-3 max-w-xs truncate" title={item.ProductDescription} style={{ color: pageTheme.muted }}>
                        {item.ProductDescription}
                      </td>
                      <td className="px-4 py-3 text-center">{item.UOMName}</td>
                      <td className="px-4 py-3 text-center">{item.OpeningQty}</td>
                      <td className="px-4 py-3 text-center" style={{ color: '#16a34a' }}>{item.ReceiptQty}</td>
                      <td className="px-4 py-3 text-center" style={{ color: '#dc2626' }}>
                        {item.IssueQtyDisplay}
                      </td>
                      <td className="px-4 py-3 font-semibold text-center" style={{ color: pageTheme.text }}>{item.ClosingQty}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </>
          )}
        </div>
        
        {/* PDF Information Panel */}
        <div className="mt-6 bg-blue-50 border-l-4 border-blue-500 p-4 rounded">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-blue-500" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <p className="text-sm text-blue-700">
                <strong>Professional PDF Report:</strong> Click the "Generate PDF" button to create a professionally formatted PDF report with proper alignment, styling, and summary information. 
                The PDF will include all records with the same filters you've selected above.
              </p>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default StockSummaryReport;